<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorksheetEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    worksheet: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;
    const fields =
      props.target && targetConfig.value[props.target].fieldsToSubmit;
    return fields ? _.pick(data, fields) : data;
  };

  const formId = ref(null);
  const defaultTitle = ref($fn.value.pgettext('Worksheet', 'Edit Worksheet'));
  const targetConfig = ref({
    header: {
      title: $fn.value.pgettext('Worksheet', 'Edit Worksheet Header'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'company'],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_s7zs44wpp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_closeComponent_c9p3p3256_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };
  const act_5534025912102772_closeModal_fcncswlbf_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_5534025912102772_crud_iay8d0vax_getActionArgs = async ($event) => {
    return {
      objectType: 'worksheet',
      op: 'upsert',
      data: getPayload(props.worksheet),
    };
  };
  const act_2434514588044777_closeComponent_osxib62rt_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_s7zs44wpp =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_s7zs44wpp_getActionArgs,
    });
  const inst_evh_7315092382398562_close_g1772ywhu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_s7zs44wpp,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_g1772ywhu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_7meaue7nl =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8spvnrq43 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7meaue7nl,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8spvnrq43],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_c9p3p3256 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_c9p3p3256_getActionArgs,
    });
  const inst_evh_8564662037462133_close_5lsttc69r =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_c9p3p3256,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_5lsttc69r],
    event: 'close',
  });
  const act_5534025912102772_closeModal_fcncswlbf =
    new actionTypes.CloseModalAction({
      actionArgs: act_5534025912102772_closeModal_fcncswlbf_getActionArgs,
    });
  const inst_evh_5534025912102772_success_14yy0bs25 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeModal_fcncswlbf,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_5534025912102772_crud_iay8d0vax = new actionTypes.CRUDAction({
    actionArgs: act_5534025912102772_crud_iay8d0vax_getActionArgs,
    displayName: 'save',
    events: [inst_evh_5534025912102772_success_14yy0bs25],
  });
  const inst_evh_5534025912102772_submit_2cy7ul6bo =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_crud_iay8d0vax,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_5534025912102772_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_submit_2cy7ul6bo],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_osxib62rt =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_osxib62rt_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_og4d1zdhy =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_osxib62rt,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_og4d1zdhy],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_g1772ywhu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8spvnrq43,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_5lsttc69r,
    inst_evh_8564662037462133_close,
    inst_evh_5534025912102772_success_14yy0bs25,
    inst_evh_5534025912102772_submit_2cy7ul6bo,
    save: inst_evh_5534025912102772_submit_2cy7ul6bo,
    inst_evh_5534025912102772_submit,
    inst_evh_2434514588044777_clickPreventStop_og4d1zdhy,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_g1772ywhu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_g1772ywhu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8spvnrq43 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8spvnrq43.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_5lsttc69r = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_5lsttc69r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_14yy0bs25 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_14yy0bs25.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit_2cy7ul6bo = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit_2cy7ul6bo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_og4d1zdhy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_og4d1zdhy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WorksheetEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <div class="page d-flex flex-column">
      <div class="page__content container-fluid pt-3 h-100">
        <ItpForm
          :id="formId"
          name="worksheet"
          class="row"
          ref="mainForm"
          @submit="evh_5534025912102772_submit($event, {})"
        >
          <ItpCol>
            <UcWorksheetEditorFieldset
              :worksheet="worksheet"
              :formId="formId"
              :target="target"
            >
            </UcWorksheetEditorFieldset>
          </ItpCol>
        </ItpForm>
      </div>
    </div>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          type="submit"
          :form="forms.worksheet"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
